import { RenderGatesPass, FinalizeGatesPass } from "./passess/render-gates-pass";
import DeferredPass from "./passess/deferred-pass";
import { BackgroundPass, BackgroundRestoreStatePass } from "./passess/background-pass";
import SlideManager from "../utils/slide-manager";
import RenderWaterPass from "./passess/water-pass";
import SystemDefaults from "../system-defaults";
import getRenderData from "../helpers/get-renderdata";
import getLetterbox from "../helpers/get-letterbox";
import RenderManager from "./render-manager";
import Network from "../miniviews/network";
import ClearPass from "./passess/clear-pass";
import getExtendedObject from "../utils/extended-intersection";
import Utils from "../utils";

let lastRender = false;
const g_normalizedViewport = new THREE.Vector4();
const g_size = new THREE.Vector2();

const QuizOutlineAnswersParams = [
    { key: "correct", color: { foreground: 0x00ff00, background: 0x00ff00 } },
    { key: "wrong", color: { foreground: 0xff0000, background: 0xff0000 } },
];

class PostProcessing {
    /**
     * 
     * @param {THREE.WebGLRenderer} renderer 
     * @param {THREE.Scene} scene
     * @param {THREE.Camera} camera
     */
    constructor({ manager, renderer, scene, camera }) {
        this.manager = manager;
        this.renderer = renderer;
        this.camera = camera;
        this.scene = scene;
        this.composer = new THREE.EffectComposer(renderer);

        this.passClear = new DeferredPass(ClearPass);
        this.passRenderWater = new DeferredPass(RenderWaterPass);
        this.passRenderGates = new DeferredPass(RenderGatesPass);
        this.passFinalizeGates = new DeferredPass(FinalizeGatesPass);
        this.passRender = new DeferredPass(THREE.RenderPass)
        this.passSAO = new DeferredPass(THREE.SAOPass);
        this.passCopy = new DeferredPass(THREE.ShaderPass, THREE.CopyShader);
        this.passOutline = new DeferredPass(THREE.OutlinePass);
        this.passOutlineTouch = new DeferredPass(THREE.OutlinePass);
        this.passOutlinePreselect = new DeferredPass(THREE.OutlinePass);
        this.passOutlineQuiz = new DeferredPass(THREE.OutlinePass);
        this.passOutlineQuizAnswers = {
            correct: new DeferredPass(THREE.OutlinePass),
            wrong: new DeferredPass(THREE.OutlinePass),
        };
        this.passHighlight = new DeferredPass(THREE.OutlinePass);
        this.passFXAA = new DeferredPass(THREE.ShaderPass, THREE.FXAAShader);
        this.passTAA = new DeferredPass(THREE.TAARenderPass);
        this.passSSAA = new DeferredPass(THREE.SSAARenderPass);
        this.passBackground = new DeferredPass(BackgroundPass);
        this.passBackgroundRestoreState = new DeferredPass(BackgroundRestoreStatePass);

        this.saoBaseDistance = Utils.queryParams.get('sao_base_distance');

        Object.freeze(this);
    }

    set(doLastRender) {
        const ppData = Q3.data.setups.postprocess;

        lastRender = doLastRender;

        this.manager.updatePostProcessing(doLastRender, ppData, Q3.editMode);
    }

    get lastRender() { return lastRender; }
    set lastRender(v) { lastRender = v; }

    updatePostProcessing({
        lastRenderer,
        ppData,
        editMode,
        enableHighlight,
        enableOutline
    }) {
        while (this.composer.passes.pop()) { }

        const { width, height } = this.renderer.getSize();
        const pixelRatio = this.renderer.getPixelRatio();
        const size = g_size.set(width, height).multiplyScalar(pixelRatio);

        this.composer.setSize(size.width, size.height);

        const params = {
            ppData,
            editMode,
            size,
            width,
            height
        };

        const passOutline = this.passOutline.get();
        const passOutlinePreselect = this.passOutlinePreselect.get();
        const passOutlineQuiz = this.passOutlineQuiz.get();
        const passOutlineQuizAnswers = {
            correct: this.passOutlineQuizAnswers.correct.get(),
            wrong: this.passOutlineQuizAnswers.wrong.get(),
        };
        const passHighlight = this.passHighlight.get();
        const passOutlineTouch = this.passOutlineTouch.get();
        const capabilities = Q3.browserCapabilities.supports.postProcessing;
        const hasTAA = capabilities.taa.enabled && capabilities.taa.samples > 0;
        const hasSSAA = capabilities.ssaa.enabled && capabilities.ssaa.samples > 0;

        const sceneState = {
            hasWater: false,
            hasOutline: passOutline && passOutline.selectedObjects.length > 0,
            hasOutlinePreselect: passOutlinePreselect && passOutlinePreselect.selectedObjects.length > 0,
            hasOutlineTouch: passOutlineTouch && passOutlineTouch.selectedObjects.length > 0,
            hasOutlineQuiz: passOutlineQuiz && passOutlineQuiz.selectedObjects.length > 0,
            hasOutlineQuizAnswers: {
                correct: passOutlineQuizAnswers.correct && passOutlineQuizAnswers.correct.selectedObjects.length > 0,
                wrong: passOutlineQuizAnswers.wrong && passOutlineQuizAnswers.wrong.selectedObjects.length > 0,
            },
            hasHighlight: passHighlight && passHighlight.selectedObjects.length > 0,
            hasGates: Network.parent || Network.gates.length > 0,
            hasBackground: this.manager.background.visible && this.manager.background.backgroundType === "panorama"
        };

        getSceneState(this.scene, sceneState);

        const backgroundState = this.getRenderState(params);

        if (sceneState.hasGates) addRenderGatesPass.call(this, params);
        else addClearPass.call(this, params);

        if (sceneState.hasWater) addWaterPass.call(this, params);

        addEmptyPass.call(this, params);

        if (sceneState.hasGates) addFinalizePass.call(this, params);

        if (hasTAA) addTAAPass.call(this, capabilities.taa.samples, params);
        else if (hasSSAA) addSSAAPass.call(this, capabilities.ssaa.samples, params);


        const slide = SlideManager.currentSlide;
        const outlinePass = slide.outlinePass || {};
        const outlinePassStyle = outlinePass.highlight || {};

        if (editMode && enableOutline) {
            const settings = Object.assign({}, outlinePassStyle);

            addOutlinePass.call(this, sceneState.hasOutline, params, SystemDefaults.getHighlightSettings(settings), this.passOutline);
            addOutlinePass.call(this, sceneState.hasOutlinePreselect, params, SystemDefaults.getHighlightSettings(settings), this.passOutlinePreselect);
            addTouchOutlinePass.call(this, sceneState.hasOutlineTouch, params, SystemDefaults.getHighlightSettings(settings));
        }

        addOutlinePass.call(this,
            sceneState.hasOutlineQuiz, params,
            SystemDefaults.getHighlightSettings(Object.assign({}, outlinePassStyle, {
                pulsePeriod: 0,
                edgeThickness: 2,
                edgeGlow: 5
            })),
            this.passOutlineQuiz
        );

        for (const { key, color: { foreground: fColor, background: bColor } } of QuizOutlineAnswersParams) {
            addOutlinePass.call(this,
                sceneState.hasOutlineQuizAnswers[key], params,
                SystemDefaults.getHighlightSettings(Object.assign({}, outlinePassStyle, {
                    pulsePeriod: 0,
                    edgeThickness: 2,
                    edgeGlow: 5,
                    colorForeground: fColor,
                    colorBackground: bColor,
                })),
                this.passOutlineQuizAnswers[key]
            );
        }

        if (enableHighlight) {
            const settings = Object.assign({}, outlinePassStyle);

            if ((slide.slideQuiz && slide.slideQuiz.type === "select") || Q3.assetMode === "edit_hierarchy") {
                Object.assign(settings, {
                    pulsePeriod: 0,
                    edgeGlow: 2,
                    edgeThickness: 2
                });
            }
            if (Q3.assetMode === "edit_hierarchy") {
                Object.assign(settings, {
                    colorForeground: 0x90ecff,
                    colorBackground: 0xa4fffb
                });
            }

            addHighlightPass.call(this, sceneState.hasHighlight, params, SystemDefaults.getHighlightSettings(settings));
        }

        if (sceneState.hasBackground)
            addBackgroundPass.call(this, params, this.manager.background.webglRootObject, backgroundState);

        if (ppData.SAO && capabilities.sao.enabled) addSAO.call(this, params);

        if (!hasTAA && !hasSSAA && capabilities.fxaa.enabled)
            addFXAAPass.call(this, params);

        addCopyPass.call(this, params);

        if (sceneState.hasBackground)
            addBackgroundRestoreStatePass.call(this, backgroundState);
    }

    getRenderState({ editMode, width: canvasWidth, height: canvasHeight }) {
        const rData = getRenderData(Q3.mobileView);
        const isResized = !editMode && (rData.top > 1e-3 || rData.left > 1e-3 || rData.width < 1 - 1e-3 || rData.height < 1 - 1e-3);
        const letterbox = rData.lerping
            ? {
                x: rData.vpLeft,
                y: rData.vpTop,
                w: rData.vpWidth,
                h: rData.vpHeight,
            } : !isResized
                ? { x: 0, y: 0, w: canvasWidth, h: canvasHeight }
                : getLetterbox();

        const fullWidth = Math.round(letterbox.w);
        const fullHeight = Math.round(letterbox.h);
        const width = Math.round(rData.width * fullWidth) / canvasWidth;
        const height = Math.round(rData.height * fullHeight) / canvasHeight;
        const offsetLeft = Math.round(letterbox.x + rData.left * fullWidth) / canvasWidth;
        const offsetTop = Math.round(letterbox.y + rData.top * fullHeight) / canvasHeight;

        const viewport = RenderManager.getViewPort();
        const scissors = RenderManager.getScissor();

        const normalizedViewport = g_normalizedViewport.set(
            offsetLeft, offsetTop,
            offsetLeft + width, offsetTop + height
        );

        return {
            viewport,
            scissors,
            normalizedViewport,
            width: canvasWidth, height: canvasHeight,
            enabled: true
        };
    }
}

export default PostProcessing;
export { PostProcessing };

function finalize(pass) { this.composer.addPass(pass); }

function addRenderGatesPass() {
    this.passRenderGates.resolve(this.scene, this.camera).then(pass => {
        finalize.call(this, pass);
    });
}

function addFinalizePass() {
    this.passFinalizeGates.resolve(this.scene, this.camera).then(pass => {
        finalize.call(this, pass);
    });
}

function addFXAAPass({ size }) {
    this.passFXAA.resolve().then(pass => {
        pass.clear = false;

        pass.setSize(size.width, size.height);

        pass.material.uniforms["resolution"].value.x = 1 / size.width;
        pass.material.uniforms["resolution"].value.y = 1 / size.height;

        finalize.call(this, pass);
    });
}

function addSSAAPass(samples, { size }) {
    this.passTAA.resolve(this.scene, this.camera).then(pass => {
        pass.clear = false;
        pass.sampleLevel = samples;

        pass.setSize(size.width, size.height);

        finalize.call(this, pass);
    });
}

function addTAAPass(samples, { size }) {
    this.passTAA.resolve(this.scene, this.camera).then(pass => {
        pass.clear = false;
        pass.sampleLevel = samples;
        pass.accumulateFrames = 60;

        pass.setSize(size.width, size.height);

        finalize.call(this, pass);
    });
}

function addWaterPass() {
    this.passRenderWater.resolve(this.manager.activeScene, this.camera).then(pass => {
        finalize.call(this, pass);
    });
}

function addEmptyPass() {
    this.passRender.resolve(this.scene, this.camera).then(pass => {
        pass.clear = false;
        finalize.call(this, pass);
    });
}

function addClearPass() {
    this.passClear.resolve().then(pass => {
        finalize.call(this, pass);
    });
}

function addBackgroundRestoreStatePass(backgroundState) {
    this.passBackgroundRestoreState.resolve().then(pass => {
        pass.setBackgroundState(backgroundState);

        finalize.call(this, pass);
    });
}

function addBackgroundPass({ size: { width, height } }, background, backgroundState) {
    this.passBackground.resolve(this.camera, background, width, height).then(pass => {

        pass.setBackgroundState(backgroundState);
        pass.setSize(width, height);

        finalize.call(this, pass);
    });
}

function addTouchOutlinePass(enabled, { size }, { colorForeground, colorBackground,
    edgeGlow, edgeThickness, edgeStrength, pulsePeriod }) {
    this.passOutlineTouch.resolve(size, this.scene, this.camera).then(pass => {
        if (!enabled) return;

        if (pass.selectedObjects.length > 0 && pass.pulsePeriod !== 0)
            this.manager.containsAnimatedObjects = true;

        pass.isTouchPass = true;
        pass.getExtendedObject = getExtendedObject;
        pass.hiddenEdgeColor.setHex(colorBackground);
        pass.visibleEdgeColor.setHex(colorForeground);
        pass.depthMaterial.morphTargets = true;

        pass.edgeGlow = edgeGlow;
        pass.edgeThickness = edgeThickness;
        pass.edgeStrength = edgeStrength;
        pass.pulsePeriod = pulsePeriod;

        finalize.call(this, pass);

        pass.setSize(size.width, size.height);
    });
}

function addOutlinePass(enabled, { size }, { colorForeground, colorBackground,
    edgeGlow, edgeThickness, edgeStrength, pulsePeriod }, deferredPass) {
    deferredPass.resolve(size, this.scene, this.camera).then(pass => {
        if (!enabled) return;

        if (pass.selectedObjects.length > 0 && pass.pulsePeriod !== 0)
            this.manager.containsAnimatedObjects = true;

        pass.hiddenEdgeColor.setHex(colorBackground);
        pass.visibleEdgeColor.setHex(colorForeground);
        pass.depthMaterial.morphTargets = true;

        pass.edgeGlow = edgeGlow;
        pass.edgeThickness = edgeThickness;
        pass.edgeStrength = edgeStrength;
        pass.pulsePeriod = pulsePeriod;

        finalize.call(this, pass);

        pass.setSize(size.width, size.height);
    });
}

function addHighlightPass(enabled, { size }, { colorForeground, colorBackground,
    edgeGlow, edgeThickness, edgeStrength, pulsePeriod }) {
    this.passHighlight.resolve(size, this.scene, this.camera).then(pass => {
        if (!enabled) return;

        // pass.renderToScreen = true;
        if (pass.selectedObjects.length > 0 && pass.pulsePeriod !== 0)
            this.manager.containsAnimatedObjects = true;

        pass.hiddenEdgeColor.setHex(colorBackground);
        pass.visibleEdgeColor.setHex(colorForeground);
        pass.depthMaterial.morphTargets = true;

        pass.edgeGlow = edgeGlow;
        pass.edgeThickness = edgeThickness;
        pass.edgeStrength = edgeStrength;
        pass.pulsePeriod = pulsePeriod;

        finalize.call(this, pass);

        pass.setSize(size.width, size.height);
    });
}

function addSAO({ ppData }) {
    this.passSAO.resolve(this.scene, this.camera, false, true, undefined, this.saoBaseDistance).then(pass => {
        pass.params.saoBias = ppData.SAO.saoBias;
        pass.params.saoIntensity = ppData.SAO.saoIntensity;
        pass.params.saoScale = ppData.SAO.saoScale;
        pass.params.saoKernelRadius = ppData.SAO.saoKernelRadius;
        pass.params.saoMinResolution = ppData.SAO.saoMinResolution;
        pass.params.saoBlur = ppData.SAO.saoBlur;
        pass.params.saoBlurRadius = ppData.SAO.saoBlurRadius;
        pass.params.saoBlurStdDev = ppData.SAO.saoBlurStdDev;
        pass.params.saoBlurDepthCutoff = ppData.SAO.saoBlurDepthCutoff;

        finalize.call(this, pass);
    });
}

function addCopyPass() {
    this.passCopy.resolve().then(pass => {
        pass.renderToScreen = true;

        finalize.call(this, pass);
    });
}

function getSceneState(object, accumulator) {
    if (!object.visible) return;

    if (object.isWater) accumulator.hasWater = true;

    object.children.forEach(object => getSceneState(object, accumulator));
}